import { useState, useEffect } from "react";
import { toast } from "sonner";
import { useUserData } from "../../service/api/useUserData";
import useSelectedDomain from "../../service/Hooks/useSelectedDomain";
import { getAccounts } from "../../service/api/Order";
import { getSecuritiesByDomainMonetary, getSecurityById } from "../../service/api/SecurityService";
import { rfiCreate, updateRfi } from "../../service/api/RfiService";
import SearchableSelect from '../../SearchableSelect';

// Sous-composant pour la sélection du titre (seulement en mode création)
const SecuritySelector = ({ value, onChange, error, securities, isEditMode }) => {
  if (isEditMode) {
    return <input type="hidden" name="securityId" value={value} />;
  }
  return (
    <div className="mb-3">
      <label className="form-label">Titre</label>
      <SearchableSelect
        options={securities}
        value={value}
        onChange={onChange}
        labelKey="name"
        valueKey="id"
        searchKeys={["name"]}
        placeholder="Sélectionner une security"
        className={`form-select form-control-lg ${error ? "is-invalid" : ""}`}
      />
      {error && <div className="invalid-feedback">{error}</div>}
    </div>
  );
};

// Sous-composant pour la sélection du compte
const AccountSelector = ({ value, onChange, error, accounts }) => (
  <div className="mb-3">
    <label>Compte</label>
    <SearchableSelect
      options={accounts}
      value={value}
      onChange={onChange}
      labelKey={(acc) => (acc.firstName || acc.lastName ? `${acc.firstName} ${acc.lastName}` : acc.companyName)}
      valueKey="id"
      searchKeys={["firstName", "lastName", "companyName"]}
      placeholder="Sélectionner un compte"
      className={`form-select form-control-lg ${error ? "is-invalid" : ""}`}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

// Sous-composant pour la sélection du type d'ordre (side)
const SideSelector = ({ value, onChange, error }) => (
  <div className="mb-3">
    <label>Type d’ordre</label>
    <select
      name="side"
      value={value}
      onChange={onChange}
      className={`form-select form-control-lg ${error ? "is-invalid" : ""}`}
    >
      <option value="">Sélectionner le type d’ordre</option>
      <option value="Buy">Achat</option>
      <option value="Sell">Vente</option>
    </select>
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

// Sous-composant pour l'input du prix
const PriceInput = ({ value, onChange, error }) => (
  <div className="mb-3">
    <label>Prix</label>
    <input
      type="number"
      name="price"
      value={value}
      onChange={onChange}
      className={`form-control ${error ? "is-invalid" : ""}`}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

// Sous-composant pour l'input de la quantité
const QuantityInput = ({ value, onChange, error }) => (
  <div className="mb-3">
    <label>Quantité</label>
    <input
      type="number"
      name="quantity"
      value={value}
      onChange={onChange}
      className={`form-control ${error ? "is-invalid" : ""}`}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

// Sous-composant pour l'input de la quantité minimale
const MinQuantityInput = ({ value, onChange, error }) => (
  <div className="mb-3">
    <label>Min Quantité</label>
    <input
      type="number"
      name="minQuantity"
      value={value}
      onChange={onChange}
      className={`form-control ${error ? "is-invalid" : ""}`}
    />
    {error && <div className="invalid-feedback">{error}</div>}
  </div>
);

export default function FormulaireRfi({ initialData, onClose }) {
  const isEditMode = initialData != null;
  const selectedDomain = useSelectedDomain();
  const { getUserInfo } = useUserData();

  const [accounts, setAccounts] = useState([]);
  const [securities, setSecurities] = useState([]);
  const [selectedSecurity, setSelectedSecurity] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [creatorId, setCreatorId] = useState(null);

  const emptyForm = {
    securityId: "",
    creatorId: "",
    fromAccountId: "",
    side: "",
    price: "",
    quantity: "",
    minQuantity: "",
    status: "Pending",
  };

  const [formData, setFormData] = useState({
    ...emptyForm,
    securityId: initialData?.securityId || "",
    creatorId: initialData?.creatorId || "",
    fromAccountId: initialData?.fromAccountId || "",
    side: initialData?.side || "",
    price: initialData?.price || "",
    quantity: initialData?.quantity || "",
    minQuantity: initialData?.minQuantity || "",
    status: initialData?.status || "Pending",
  });

  useEffect(() => {
    async function fetchUserData() {
      const userInfo = await getUserInfo();
      if (userInfo) {
        setCreatorId(userInfo.accountId);
        setFormData((prev) => ({ ...prev, creatorId: userInfo.accountId }));
      }
    }
    fetchUserData();
  }, [getUserInfo]);

  useEffect(() => {
    if (!selectedDomain || !selectedDomain.id) return;

    async function fetchData() {
      try {
        const accountsData = await getAccounts(selectedDomain.id);
        setAccounts(accountsData.items);
        const params = {
          pageNumber: 1,
          pageSize: 500,
        };
        const securityData = await getSecuritiesByDomainMonetary(params);
        const filteredSecurities = securityData.items.filter(sec => sec.targetMarket === "Monetary");
        setSecurities(filteredSecurities);
        setSecurities(filteredSecurities);
      } catch  {
        // toast.error("Erreur lors du chargement des données.");
      }
    }

    fetchData();
  }, [selectedDomain]);

  useEffect(() => {
    async function fetchSecurity() {
      if (formData.securityId) {
        try {
          const sec = await getSecurityById(formData.securityId);
          setSelectedSecurity(sec);
        } catch (error) {
          toast.error("Erreur lors de la récupération des titres.");
          setSelectedSecurity(null);
        }
      } else {
        setSelectedSecurity(null);
      }
    }
    fetchSecurity();
  }, [formData.securityId]);

  useEffect(() => {
    if (selectedSecurity) {
      if (formData.price && formData.price > selectedSecurity.lastPrice) {
        setErrors((prev) => ({
          ...prev,
          price: `Le prix ne peut pas dépasser le prix de la security (${selectedSecurity.lastPrice}).`,
        }));
      } else {
        setErrors((prev) => ({ ...prev, price: "" }));
      }
      if (formData.quantity && formData.quantity > selectedSecurity.totalUnitsIssue) {
        setErrors((prev) => ({
          ...prev,
          quantity: `La quantité ne peut pas dépasser la quantité de la security (${selectedSecurity.totalUnitsIssue}).`,
        }));
      } else {
        setErrors((prev) => ({ ...prev, quantity: "" }));
      }
    }
  }, [selectedSecurity, formData.price, formData.quantity]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newValue = ["price", "quantity", "minQuantity"].includes(name)
      ? value === ""
        ? ""
        : parseFloat(value)
      : value;
    setFormData((prev) => ({ ...prev, [name]: newValue }));
    validateField(name, newValue);
  };

  const handleSecurityChange = (selected) => {
    const securityId = selected?.id || "";
    setFormData((prev) => ({ ...prev, securityId }));
    validateField("securityId", securityId);
  };

  const handleAccountChange = (selected) => {
    const fromAccountId = selected?.id || "";
    setFormData((prev) => ({ ...prev, fromAccountId }));
    validateField("fromAccountId", fromAccountId);
  };

  const validateField = (name, value) => {
    let error = "";

    if (["securityId", "fromAccountId", "side"].includes(name) && !value) {
      error = "Ce champ est requis.";
    }

    if (["price", "quantity", "minQuantity"].includes(name)) {
      if (value === "" || isNaN(value) || value <= 0) {
        error = "Doit être un nombre positif.";
      }
      if (name === "quantity") {
        const minQuantity = parseFloat(formData.minQuantity);
        if (!isNaN(minQuantity) && value < minQuantity) {
          error = `La quantité doit être supérieure ou égale à la quantité minimale (${minQuantity}).`;
        }
      }
      if (name === "minQuantity") {
        const quantity = parseFloat(formData.quantity);
        if (!isNaN(quantity) && value > quantity) {
          error = `La quantité minimale ne peut pas être supérieure à la quantité (${quantity}).`;
        }
      }
      if (selectedSecurity) {
        if (name === "price" && value > selectedSecurity.lastPrice) {
          error = `Le prix ne peut pas dépasser le prix de la security (${selectedSecurity.lastPrice}).`;
        }
        if (name === "quantity" && value > selectedSecurity.totalUnitsIssue) {
          error = `La quantité ne peut pas dépasser la quantité de la security (${selectedSecurity.totalUnitsIssue}).`;
        }
      }
    }

    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const newErrors = {};
    let hasErrors = false;
    Object.keys(formData).forEach((key) => {
      let value = formData[key];
      let error = "";
      if (["securityId", "fromAccountId", "side"].includes(key) && !value) {
        error = "Ce champ est requis.";
      }
      if (["price", "quantity", "minQuantity"].includes(key)) {
        if (value === "" || isNaN(value) || value <= 0) {
          error = "Doit être un nombre positif.";
        }
      }
      if (selectedSecurity) {
        if (key === "price" && value > selectedSecurity.lastPrice) {
          error = `Le prix ne peut pas dépasser le prix de la security (${selectedSecurity.lastPrice}).`;
        }
        if (key === "quantity" && value > selectedSecurity.totalUnitsIssue) {
          error = `La quantité ne peut pas dépasser la quantité de la security (${selectedSecurity.totalUnitsIssue}).`;
        }
      }
      if (error) {
        newErrors[key] = error;
        hasErrors = true;
      }
    });
    setErrors(newErrors);
    if (hasErrors) return;

    const dataToSend = {
      ...formData,
      securityId: parseInt(formData.securityId, 10),
    };

    setLoading(true);
    try {
      if (isEditMode) {
        await updateRfi(initialData.id, dataToSend);
        toast.success("RFI modifiée avec succès.");
      } else {
        await rfiCreate(dataToSend);
        toast.success("RFI créée avec succès.");
        setFormData({ ...emptyForm, creatorId });
      }
      if (onClose) {
        onClose();
      }
    } catch (error) {
      toast.error(isEditMode ? "Erreur lors de la modification de la RFI." : "Erreur lors de la création de la RFI.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="modal fade" id="newRfi" tabIndex="-1" aria-labelledby="rfiModalLabel" aria-hidden="true">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title" id="rfiModalLabel">
              {isEditMode ? "Modifier RFI" : "Nouvelle RFI"}
            </h5>
            <button type="button" className="btn-close" data-bs-dismiss="modal" aria-label="Close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <form onSubmit={handleSubmit}>
              <input type="hidden" name="creatorId" value={formData.creatorId} />
              <SecuritySelector
                value={formData.securityId}
                onChange={handleSecurityChange}
                error={errors.securityId}
                securities={securities}
                isEditMode={isEditMode}
              />
              <AccountSelector
                value={formData.fromAccountId}
                onChange={handleAccountChange}
                error={errors.fromAccountId}
                accounts={accounts}
              />
              <SideSelector
                value={formData.side}
                onChange={handleChange}
                error={errors.side}
              />
              <PriceInput
                value={formData.price}
                onChange={handleChange}
                error={errors.price}
              />
              <QuantityInput
                value={formData.quantity}
                onChange={handleChange}
                error={errors.quantity}
              />
              <MinQuantityInput
                value={formData.minQuantity}
                onChange={handleChange}
                error={errors.minQuantity}
              />
              <button type="submit" className="btn btn-primary" disabled={loading}>
                {loading ? (isEditMode ? "Modification..." : "Création...") : (isEditMode ? "Modifier la RFI" : "Créer la RFI")}
              </button>
            </form>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-danger" data-bs-dismiss="modal" onClick={onClose}>
              Fermer
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}